/**
 * Dreamweaver · v3.49b · Phase A minimal viewer.
 *
 * The quiet room where a member whispers what they've always wanted
 * — and, over time, watches the concierge weave it into being.
 *
 * PHASE A scope (this file):
 *   - Two panels · Whisper (left) + Journal (right)
 *   - Text-only input (voice + photos arrive in Phase B)
 *   - Visibility toggle at capture (share / private_journal) with copy verbatim
 *   - Consent checkboxes (store_process required)
 *   - 12 seed inspiration cards above the input
 *   - Journal list of the member's own dreams with status pill
 *   - No Sonnet echo, no price/availability, no admin surfaces (§4.1)
 *
 * PHASE B (not this file):
 *   - Voice recorder + Whisper transcription
 *   - Cloudinary mood-board upload
 *   - Realized celebration animation
 *   - MC embed variant
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import MemberContextBand from "../components/MemberContextBand";
import CldImg from "../components/CldImg";
import { resizeImagesForUpload } from "@/lib/imageResize";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// ── Palette · thin, tender, warm cream + brass ──────────────────────
const T = {
  ink:      "#1a1a1a",
  mist:     "#f7f4ee",
  cream:    "#fbf9f4",
  brass:    "#a97e2d",
  brassSoft:"#c9a663",
  border:   "rgba(26,26,26,0.08)",
  muted:    "rgba(26,26,26,0.55)",
};

const STATUS_PILL = {
  Whispered:  { bg: "#f0ebe0", fg: "#8a6b2c" },
  Weaving:    { bg: "#e6efe0", fg: "#4a6b32" },
  Realizing:  { bg: "#e0e9ef", fg: "#2c5a7a" },
  Realized:   { bg: "#f4e0e6", fg: "#8a2c5a" },
  Reimagined: { bg: "#ede0ef", fg: "#5a2c8a" },
  Paused:     { bg: "#efebe0", fg: "#6b5a2c" },
  Archived:   { bg: "#e8e4dc", fg: "#605a52" },
};

export default function DreamweaverPage() {
  const [inspirations, setInspirations] = useState([]);
  const [journal, setJournal]           = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  const [text, setText]         = useState("");
  const [urgency, setUrgency]   = useState("someday");
  const [visibility, setVis]    = useState("shared");
  const [consentStore, setCS]   = useState(false);
  const [submitting, setSub]    = useState(false);

  // ── Voice + Photos state (Phase B) ───────────────────────────────
  const [photos, setPhotos]         = useState([]);   // [{url, public_id}]
  const [photoBusy, setPhotoBusy]   = useState(false);
  const [consentPhotos, setCPh]     = useState(false);

  const [voiceUrl, setVoiceUrl]     = useState("");
  const [voiceRecording, setVR]     = useState(false);
  const [voiceBusy, setVB]          = useState(false);
  const [consentVoice, setCV]       = useState(false);
  const mediaRef                    = React.useRef(null);
  const chunksRef                   = React.useRef([]);

  // ── Load inspirations + journal on mount ──────────────────────────
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      // Load in parallel but allow either to fail independently — the
      // /journal endpoint 401s for unauthenticated visitors and that
      // must not blank the inspirations shelf.
      const [inspRes, jnRes] = await Promise.allSettled([
        axios.get(`${API}/api/dreamweaver/inspirations`),
        axios.get(`${API}/api/dreamweaver/journal`, { withCredentials: true }),
      ]);
      if (cancel) return;
      if (inspRes.status === "fulfilled") {
        setInspirations(inspRes.value.data.inspirations || []);
      }
      if (jnRes.status === "fulfilled") {
        setJournal(jnRes.value.data.dreams || []);
        setTotal(jnRes.value.data.total || 0);
      }
      // Do NOT set `error` from the mount-time journal 401 — that's the
      // expected unauthenticated state, not a whisper failure.
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  const seedByRegister = useMemo(() => {
    const out = { close: [], medium: [], far: [], legacy: [] };
    inspirations.forEach((i) => { if (out[i.register]) out[i.register].push(i); });
    return out;
  }, [inspirations]);

  // ── Whisper submit ─────────────────────────────────────────────────
  async function whisper(e) {
    e?.preventDefault?.();
    setError("");
    if (!consentStore)         return setError("Please tick the box below before whispering.");
    if (text.trim().length < 3) return setError("A dream needs a few words to travel.");
    if (photos.length > 0 && !consentPhotos) return setError("Please confirm you'd like your photos held with the dream.");
    if (voiceUrl && !consentVoice) return setError("Please confirm the voice note may be transcribed.");
    setSub(true);
    try {
      const body = {
        dream_text: text.trim(),
        urgency,
        consent: {
          store_process: { given: true },
          share_choice:  visibility,
          ...(consentPhotos && photos.length > 0 ? { photos: { given: true } } : {}),
          ...(consentVoice  && voiceUrl         ? { voice_transcription: { given: true } } : {}),
        },
        ...(photos.length > 0 ? { mood_photo_urls: photos.map((p) => p.url) } : {}),
        ...(voiceUrl          ? { voice_url:       voiceUrl } : {}),
      };
      const r = await axios.post(`${API}/api/dreamweaver/whisper`, body, { withCredentials: true });
      setJournal((j) => [r.data, ...j]);
      setTotal((n) => n + 1);
      setText("");
      setPhotos([]);
      setVoiceUrl("");
      setCPh(false); setCV(false);
      setConsentStore(false); // eslint-disable-line no-undef
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSub(false);
    }
  }
  function setConsentStore(v) { setCS(v); }

  // ── Photo upload ─────────────────────────────────────────────────
  async function onPhotoPick(e) {
    const rawFiles = Array.from(e.target.files || []);
    if (!rawFiles.length) return;
    if (photos.length + rawFiles.length > 3) {
      setError("Up to three photos per dream.");
      e.target.value = "";
      return;
    }
    setPhotoBusy(true); setError("");
    try {
      // v3.48 · Client-side resize before upload. Phone photos are
      // typically 3–8 MB (regular) or 40–75 MB (ProRAW). We resample
      // to ≤2500px on the longest edge · JPEG q=0.85. Typical output
      // is ~1–2 MB. Bypasses the ingress body-size ceiling entirely
      // and makes uploads noticeably faster on cellular.
      const files = await resizeImagesForUpload(rawFiles);
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/api/dreamweaver/upload/photo`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ url: r.data.secure_url, public_id: r.data.public_id });
      }
      setPhotos((p) => [...p, ...uploaded]);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setPhotoBusy(false);
      e.target.value = "";
    }
  }

  function removePhoto(idx) {
    setPhotos((p) => p.filter((_, i) => i !== idx));
  }

  // ── Voice recording (MediaRecorder API) ──────────────────────────
  async function startRecording() {
    setError("");
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setError("Voice recording isn't available in this browser. Try Chrome, Edge, or Safari 14+.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (evt) => { if (evt.data && evt.data.size) chunksRef.current.push(evt.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 400) {
          setError("The recording was too short. Please try again.");
          return;
        }
        await uploadVoice(blob);
      };
      mediaRef.current = rec;
      rec.start();
      setVR(true);
      // Hard cap 3 minutes.
      setTimeout(() => { try { if (rec.state === "recording") rec.stop(); } catch(_) { /* noop */ } }, 180000);
    } catch (err) {
      setError("Microphone access denied. Please allow, then try again.");
    }
  }

  function stopRecording() {
    try { mediaRef.current?.stop(); } catch (_) { /* noop */ }
    setVR(false);
  }

  async function uploadVoice(blob) {
    setVB(true); setError("");
    try {
      const fd = new FormData();
      const filename = "whisper-" + Date.now() + (blob.type.includes("webm") ? ".webm" :
                                                    blob.type.includes("mp4")  ? ".m4a"  : ".webm");
      fd.append("file", blob, filename);
      fd.append("consent_voice", consentVoice ? "true" : "true"); // FE requires tick before starting record
      const r = await axios.post(`${API}/api/dreamweaver/transcribe`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVoiceUrl(r.data.voice_url || "");
      if (r.data.transcript && r.data.transcript.trim()) {
        setText((cur) => cur.trim() ? cur.trim() + "\n\n" + r.data.transcript : r.data.transcript);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setVB(false);
    }
  }

  function clearVoice() { setVoiceUrl(""); }

  async function flipVisibility(id, next) {
    try {
      const r = await axios.patch(`${API}/api/dreamweaver/${id}/visibility`, { visibility: next }, { withCredentials: true });
      setJournal((j) => j.map((d) => (d.id === id ? r.data : d)));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  async function archive(id) {
    if (!confirm("Move this dream to your archive? You can ask a Concierge® to erase it entirely afterwards.")) return;
    try {
      const r = await axios.post(`${API}/api/dreamweaver/${id}/archive`, {}, { withCredentials: true });
      setJournal((j) => j.map((d) => (d.id === id ? r.data : d)));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div
      data-testid="dreamweaver-page"
      style={{
        minHeight: "100vh",
        background: T.cream,
        color: T.ink,
        fontFamily: '"EB Garamond", Georgia, serif',
        padding: "56px 24px 96px",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <header style={{ textAlign: "left", marginBottom: 48 }}>
          <div style={{
            fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase",
            color: T.brass, marginBottom: 12,
          }}>
            Dreamweaver
          </div>
          <h1 style={{
            fontSize: "clamp(38px, 6vw, 68px)",
            lineHeight: 1.04, fontWeight: 400, margin: 0,
            letterSpacing: "-0.01em",
          }}>
            What have you <em style={{ color: T.brass, fontStyle: "italic" }}>always</em> wanted,<br/>
            but never quite asked for?
          </h1>
          <p style={{
            marginTop: 20, fontSize: 18, lineHeight: 1.55,
            color: T.muted, maxWidth: 680,
          }}>
            Whisper it here. A Concierge® holds it, watches for the moment,
            and — when the moment arrives — weaves it into being.
          </p>
        </header>

        <MemberContextBand pillar="dreamweaver" />

        {/* ── Inspirations shelf ───────────────────────────────── */}
        {inspirations.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <div style={{
              fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
              color: T.muted, marginBottom: 14,
            }}>
              To begin · a few inspirations
            </div>
            <div style={{ display: "grid", gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
              {["close","medium","far","legacy"].flatMap((r) => (seedByRegister[r] || []).map((i) => (
                <button
                  key={i.id}
                  type="button"
                  data-testid={`insp-${i.id}`}
                  onClick={() => setText(i.text)}
                  style={{
                    textAlign: "left", cursor: "pointer",
                    background: T.mist, border: `1px solid ${T.border}`,
                    borderRadius: 10, padding: "16px 18px",
                    fontFamily: "inherit", color: T.ink,
                    fontSize: 15, lineHeight: 1.5,
                    transition: "border-color .15s, transform .15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brassSoft; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; }}
                >
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em",
                                color: T.brass, marginBottom: 8 }}>
                    {i.register === "legacy" ? "· quiet" : i.register === "far" ? "· someday" :
                     i.register === "medium" ? "· this year" : "· soon"}
                  </div>
                  <div>{i.text}</div>
                </button>
              )))}
            </div>
          </section>
        )}

        {/* ── Two-panel: Whisper (left) + Journal (right) ──────── */}
        <div style={{ display: "grid", gap: 40,
          gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 0.85fr)" }}>

          {/* Whisper panel */}
          <section data-testid="whisper-panel">
            <div style={{
              fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
              color: T.muted, marginBottom: 14,
            }}>
              Whisper a dream
            </div>
            <form onSubmit={whisper} style={{
              background: "white", border: `1px solid ${T.border}`,
              borderRadius: 14, padding: 24,
            }}>
              <textarea
                data-testid="whisper-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Say it in your own words. Long, short, unfinished — anything."
                rows={6}
                style={{
                  width: "100%", border: 0, outline: 0, resize: "vertical",
                  fontSize: 18, lineHeight: 1.6, fontFamily: "inherit",
                  color: T.ink, background: "transparent",
                }}
              />

              {/* Urgency ─────────────────────────────────── */}
              <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[
                  { k: "someday",    label: "Someday" },
                  { k: "this_year",  label: "This year" },
                  { k: "right_now",  label: "Right now" },
                ].map((u) => (
                  <button
                    key={u.k}
                    type="button"
                    data-testid={`urgency-${u.k}`}
                    onClick={() => setUrgency(u.k)}
                    style={{
                      padding: "6px 14px", borderRadius: 999,
                      border: `1px solid ${urgency === u.k ? T.brass : T.border}`,
                      background: urgency === u.k ? T.brass : "transparent",
                      color:      urgency === u.k ? "white"  : T.ink,
                      fontFamily: "inherit", fontSize: 13, cursor: "pointer",
                      transition: "all .15s",
                    }}
                  >{u.label}</button>
                ))}
              </div>

              {/* Voice + Photos (Phase B) ────────────────── */}
              <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 14,
                            padding: 14, background: T.mist, borderRadius: 10, border: `1px solid ${T.border}` }}>
                {/* Voice consent + button */}
                <div>
                  <label style={{
                    display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13,
                    color: T.muted, cursor: "pointer", marginBottom: 8,
                  }}>
                    <input type="checkbox"
                      data-testid="consent-voice"
                      checked={consentVoice}
                      onChange={(e) => setCV(e.target.checked)}
                      style={{ marginTop: 3 }} />
                    <span>
                      I'd like to whisper aloud · Concierge® may transcribe it via a secure model
                      to help capture the feeling. <a href="/legal" target="_blank" rel="noopener noreferrer"
                        style={{ color: T.brass }}>How voice is held</a>
                    </span>
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    {!voiceRecording ? (
                      <button type="button"
                        data-testid="voice-record"
                        disabled={!consentVoice || voiceBusy || !!voiceUrl}
                        onClick={startRecording}
                        style={{
                          padding: "8px 16px", borderRadius: 999,
                          border: `1px solid ${T.brass}`, background: "white", color: T.brass,
                          fontFamily: "inherit", fontSize: 13, cursor: consentVoice ? "pointer" : "not-allowed",
                          opacity: consentVoice ? 1 : 0.5,
                        }}>
                        {voiceUrl ? "Voice attached" : voiceBusy ? "Transcribing…" : "● Record voice note"}
                      </button>
                    ) : (
                      <button type="button"
                        data-testid="voice-stop"
                        onClick={stopRecording}
                        style={{
                          padding: "8px 16px", borderRadius: 999,
                          border: `1px solid ${T.brass}`, background: T.brass, color: "white",
                          fontFamily: "inherit", fontSize: 13, cursor: "pointer",
                        }}>
                        ■ Stop recording
                      </button>
                    )}
                    {voiceUrl && (
                      <>
                        <audio data-testid="voice-preview" controls src={voiceUrl}
                               style={{ height: 30, maxWidth: 280 }} />
                        <button type="button"
                          data-testid="voice-clear"
                          onClick={clearVoice}
                          style={{
                            background: "transparent", border: 0, cursor: "pointer",
                            color: T.muted, fontFamily: "inherit", fontSize: 12,
                            textTransform: "uppercase", letterSpacing: "0.14em",
                          }}>Remove</button>
                      </>
                    )}
                    {voiceRecording && (
                      <span style={{ color: T.brass, fontSize: 13, fontStyle: "italic" }}>
                        Listening… speak softly, this is between us.
                      </span>
                    )}
                  </div>
                </div>

                {/* Photos */}
                <div>
                  <label style={{
                    display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13,
                    color: T.muted, cursor: "pointer", marginBottom: 8,
                  }}>
                    <input type="checkbox"
                      data-testid="consent-photos"
                      checked={consentPhotos}
                      onChange={(e) => setCPh(e.target.checked)}
                      style={{ marginTop: 3 }} />
                    <span>
                      I'd like to include a small mood board (up to three photos) so Concierge®
                      can feel the shape of the dream.
                    </span>
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <input
                      type="file" accept="image/*" multiple
                      id="dw-photo-input"
                      data-testid="photo-input"
                      onChange={onPhotoPick}
                      disabled={!consentPhotos || photoBusy || photos.length >= 3}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="dw-photo-input"
                      data-testid="photo-label"
                      style={{
                        padding: "8px 16px", borderRadius: 999,
                        border: `1px solid ${T.brass}`, background: "white", color: T.brass,
                        fontFamily: "inherit", fontSize: 13,
                        cursor: (!consentPhotos || photos.length >= 3) ? "not-allowed" : "pointer",
                        opacity: (!consentPhotos || photos.length >= 3) ? 0.5 : 1,
                      }}>
                      {photoBusy ? "Uploading…" : photos.length >= 3 ? "Three photos on board" : "+ Add photo"}
                    </label>
                    {photos.map((p, i) => (
                      <div key={p.url} data-testid={`photo-thumb-${i}`}
                        style={{ position: "relative", width: 64, height: 64,
                                 borderRadius: 8, overflow: "hidden",
                                 border: `1px solid ${T.border}` }}>
                        <CldImg src={p.url} w={128} alt="mood"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button"
                          onClick={() => removePhoto(i)}
                          data-testid={`photo-remove-${i}`}
                          style={{
                            position: "absolute", top: 2, right: 2, width: 20, height: 20,
                            borderRadius: "50%", border: 0, cursor: "pointer",
                            background: "rgba(26,26,26,0.7)", color: "white", fontSize: 12,
                            lineHeight: 1, padding: 0,
                          }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Visibility (share choice) ────────────────── */}
              <fieldset style={{ marginTop: 24, border: 0, padding: 0 }}>
                <legend style={{ fontSize: 11, letterSpacing: "0.28em",
                  textTransform: "uppercase", color: T.muted, marginBottom: 10 }}>
                  Who may hold this
                </legend>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10,
                  fontSize: 15, cursor: "pointer", marginBottom: 8 }}>
                  <input type="radio" name="vis" value="shared"
                    data-testid="visibility-shared"
                    checked={visibility === "shared"}
                    onChange={() => setVis("shared")}
                    style={{ marginTop: 4 }} />
                  <span>
                    Share with my Concierge® so we can watch for the moment
                    <span style={{ color: T.brass, marginLeft: 6, fontSize: 12,
                      textTransform: "uppercase", letterSpacing: "0.2em" }}>· recommended</span>
                  </span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10,
                  fontSize: 15, cursor: "pointer" }}>
                  <input type="radio" name="vis" value="private_journal"
                    data-testid="visibility-private"
                    checked={visibility === "private_journal"}
                    onChange={() => setVis("private_journal")}
                    style={{ marginTop: 4 }} />
                  <span>Keep this private in my journal for now — I'll share when I'm ready</span>
                </label>
              </fieldset>

              {/* Consent · store_process ─────────────────── */}
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                marginTop: 24, fontSize: 14, color: T.muted, cursor: "pointer",
              }}>
                <input type="checkbox"
                  data-testid="consent-store"
                  checked={consentStore}
                  onChange={(e) => setConsentStore(e.target.checked)}
                  style={{ marginTop: 3 }} />
                <span>
                  I'm happy for Concierge® to hold this dream and work on it for me.{" "}
                  <a href="/legal" target="_blank" rel="noopener noreferrer"
                     style={{ color: T.brass }}>Privacy Policy</a>
                </span>
              </label>

              {error && (
                <div data-testid="whisper-error"
                  style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8,
                    background: "#fdecea", color: "#8a2c1f", fontSize: 14 }}>
                  {error}
                </div>
              )}

              <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  data-testid="whisper-submit"
                  disabled={submitting || !consentStore || text.trim().length < 3}
                  style={{
                    padding: "12px 28px", borderRadius: 999,
                    border: `1px solid ${T.brass}`,
                    background: submitting ? T.brassSoft : T.brass,
                    color: "white", fontFamily: "inherit", fontSize: 15,
                    letterSpacing: "0.06em",
                    cursor: (submitting || !consentStore) ? "not-allowed" : "pointer",
                    opacity: (submitting || !consentStore) ? 0.55 : 1,
                    transition: "opacity .15s",
                  }}
                >
                  {submitting ? "Weaving your whisper…" : "Whisper this dream"}
                </button>
              </div>
            </form>
          </section>

          {/* Journal panel */}
          <section data-testid="journal-panel">
            <div style={{
              fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
              color: T.muted, marginBottom: 14,
            }}>
              Your dream journal · {total}
            </div>

            {loading && <div style={{ color: T.muted, fontSize: 14 }}>Turning the pages…</div>}
            {!loading && journal.length === 0 && (
              <div style={{
                background: T.mist, border: `1px dashed ${T.border}`,
                borderRadius: 12, padding: 28, color: T.muted, fontSize: 15,
                lineHeight: 1.6, fontStyle: "italic",
              }}>
                Every dream you whisper will live quietly here.
                The Concierge® will hold it, gently — until the moment finds you.
              </div>
            )}

            <div style={{ display: "grid", gap: 12 }}>
              {journal.map((d) => {
                const pill = STATUS_PILL[d.status] || STATUS_PILL.Whispered;
                return (
                  <article
                    key={d.id}
                    data-testid={`journal-card-${d.id}`}
                    style={{
                      background: "white", border: `1px solid ${T.border}`,
                      borderRadius: 12, padding: 20,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
                      <span style={{
                        padding: "3px 10px", borderRadius: 999,
                        background: pill.bg, color: pill.fg,
                        fontSize: 10, letterSpacing: "0.2em",
                        textTransform: "uppercase", fontWeight: 500,
                      }}>{d.status}</span>
                      <span style={{ fontSize: 11, color: T.muted, textTransform: "uppercase",
                        letterSpacing: "0.16em" }}>
                        {d.urgency === "right_now"  ? "· right now" :
                         d.urgency === "this_year"  ? "· this year"  : "· someday"}
                        {d.visibility === "private_journal" ? "  ·  private" : ""}
                      </span>
                    </div>

                    <div style={{ fontSize: 16, lineHeight: 1.55, color: T.ink }}>
                      {d.dream_text}
                    </div>

                    {d.realized_at && (
                      <div data-testid={`realized-${d.id}`} style={{
                        marginTop: 10, fontSize: 13, color: pill.fg, fontStyle: "italic",
                        display: "flex", alignItems: "center", gap: 8,
                        animation: "dwRealizedFadeIn 1.2s ease-out",
                      }}>
                        <span style={{
                          display: "inline-block", fontSize: 16,
                          animation: "dwSparkle 2.4s ease-in-out infinite",
                        }}>✦</span>
                        Realized on {new Date(d.realized_at).toLocaleDateString(undefined,
                          { day: "numeric", month: "long", year: "numeric" })}
                      </div>
                    )}

                    <div style={{
                      marginTop: 14, paddingTop: 12,
                      borderTop: `1px solid ${T.border}`,
                      display: "flex", gap: 12, flexWrap: "wrap",
                      fontSize: 12,
                    }}>
                      {d.visibility === "private_journal" ? (
                        <button
                          type="button"
                          data-testid={`share-${d.id}`}
                          onClick={() => flipVisibility(d.id, "shared")}
                          style={{
                            background: "transparent", border: 0, cursor: "pointer",
                            color: T.brass, fontFamily: "inherit", fontSize: 12,
                            letterSpacing: "0.1em", textTransform: "uppercase",
                          }}
                        >
                          Share with my Concierge®
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`private-${d.id}`}
                          onClick={() => flipVisibility(d.id, "private_journal")}
                          style={{
                            background: "transparent", border: 0, cursor: "pointer",
                            color: T.muted, fontFamily: "inherit", fontSize: 12,
                            letterSpacing: "0.1em", textTransform: "uppercase",
                          }}
                        >
                          Keep private for now
                        </button>
                      )}
                      {d.status !== "Archived" && (
                        <button
                          type="button"
                          data-testid={`archive-${d.id}`}
                          onClick={() => archive(d.id)}
                          style={{
                            background: "transparent", border: 0, cursor: "pointer",
                            color: T.muted, fontFamily: "inherit", fontSize: 12,
                            letterSpacing: "0.1em", textTransform: "uppercase",
                            marginLeft: "auto",
                          }}
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        {/* ── Footer note ─────────────────────────────────────────── */}
        <footer style={{
          marginTop: 80, paddingTop: 32, borderTop: `1px solid ${T.border}`,
          fontSize: 13, color: T.muted, lineHeight: 1.6, textAlign: "center",
        }}>
          Nothing you see on this page is a promise until a Concierge® has made
          it true. Mira proposes; a human verifies and weaves.
        </footer>
      </div>
      {/* Keyframes for the Realized celebration */}
      <style>{`
        @keyframes dwRealizedFadeIn {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0);   }
        }
        @keyframes dwSparkle {
          0%,100% { opacity: 0.55; transform: scale(1) rotate(0deg); }
          50%     { opacity: 1;    transform: scale(1.35) rotate(20deg); }
        }
      `}</style>
    </div>
  );
}
